/**
 * VideoSettings — Modal for one-click video options (quality, duration, voice).
 * Same layout pattern as RecordingSettings. On "Generate", calls onGenerate(settings).
 */

import React, { useCallback, useEffect, useRef, useState } from "react";

import { useAppI18n } from "../hooks/useAppI18n";
import type { VideoGenSettings } from "../hooks/useRecordingState";

import "./RecordingFeature.scss";

/** Cache voice sample blob URLs so we don't re-fetch on every click. */
const voiceSampleCache = new Map<string, string>();

const QUALITY_OPTIONS: { value: VideoGenSettings["quality"]; labelKey: string }[] = [
  { value: "720p", labelKey: "recording.videoQuality720p" },
  { value: "1080p", labelKey: "recording.videoQuality1080p" },
  { value: "4k", labelKey: "recording.videoQuality4k" },
];

const DURATION_OPTIONS: ReadonlyArray<number | null> = [1, 2, 3, 5];
const DURATION_NO_PREFERENCE = null;

const VOICE_OPTIONS: { value: string; labelKey: string }[] = [
  { value: "alloy", labelKey: "recording.videoVoiceAlloy" },
  { value: "echo", labelKey: "recording.videoVoiceEcho" },
  { value: "fable", labelKey: "recording.videoVoiceFable" },
  { value: "onyx", labelKey: "recording.videoVoiceOnyx" },
  { value: "nova", labelKey: "recording.videoVoiceNova" },
  { value: "shimmer", labelKey: "recording.videoVoiceShimmer" },
];

const DEFAULT_SETTINGS: VideoGenSettings = {
  quality: "1080p",
  targetDurationMinutes: 2,
  voice: "alloy",
};


interface VideoSettingsProps {
  isOpen: boolean;
  onGenerate: (settings: VideoGenSettings) => void;
  onClose: () => void;
  voiceSampleUrlBuilder?: (voice: string) => string;
}

export const VideoSettings: React.FC<VideoSettingsProps> = ({
  isOpen,
  onGenerate,
  onClose,
  voiceSampleUrlBuilder,
}) => {
  const { t } = useAppI18n();
  const [quality, setQuality] = useState<VideoGenSettings["quality"]>(DEFAULT_SETTINGS.quality);
  const [targetDurationMinutes, setTargetDurationMinutes] = useState<number | null>(
    DEFAULT_SETTINGS.targetDurationMinutes,
  );
  const [voice, setVoice] = useState(DEFAULT_SETTINGS.voice);
  const [customDurationInput, setCustomDurationInput] = useState("");
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    return () => {
      if (previewAudioRef.current) {
        previewAudioRef.current.pause();
        previewAudioRef.current = null;
      }
      voiceSampleCache.forEach((url) => URL.revokeObjectURL(url));
      voiceSampleCache.clear();
    };
  }, []);

  const effectiveDurationMinutes =
    (() => {
      const trimmed = customDurationInput.trim();
      if (!trimmed) return targetDurationMinutes;
      const n = Number(trimmed);
      if (!Number.isFinite(n) || n <= 0) return targetDurationMinutes;
      return n;
    })();

  const handleGenerate = useCallback(() => {
    onGenerate({
      quality,
      targetDurationMinutes: effectiveDurationMinutes,
      voice,
    });
  }, [quality, effectiveDurationMinutes, voice, onGenerate]);

  const handlePresetDurationClick = useCallback((minutes: number | null) => {
    setTargetDurationMinutes(minutes);
    setCustomDurationInput("");
  }, []);

  const handleCustomDurationChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomDurationInput(e.target.value);
  }, []);

  const stopPreview = useCallback(() => {
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current.currentTime = 0;
      previewAudioRef.current = null;
    }
  }, []);

  const playVoiceSample = useCallback(
    async (v: string) => {
      if (!voiceSampleUrlBuilder) return;
      stopPreview();
      const fetchUrl = voiceSampleUrlBuilder(v);
      let blobUrl = voiceSampleCache.get(fetchUrl);
      if (!blobUrl) {
        try {
          const res = await fetch(fetchUrl);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const blob = await res.blob();
          blobUrl = URL.createObjectURL(blob);
          voiceSampleCache.set(fetchUrl, blobUrl);
        } catch (err) {
          console.warn("[VideoSettings] voice sample fetch failed:", err);
          return;
        }
      }
      const audio = new Audio(blobUrl);
      previewAudioRef.current = audio;
      audio.play().catch((err: unknown) => {
        const e = err as { name?: string; message?: string };
        if (e?.name === "AbortError") return;
        console.warn("[VideoSettings] voice sample play failed:", err);
      });
    },
    [voiceSampleUrlBuilder, stopPreview],
  );

  const handleVoiceClick = useCallback(
    (v: string) => {
      if (v === voice) {
        return;
      }
      stopPreview();
      setVoice(v);
      playVoiceSample(v);
    },
    [voice, playVoiceSample, stopPreview],
  );

  if (!isOpen) {
    return null;
  }

  return (
    <div className="recording-settings-overlay video-settings-overlay" onClick={onClose}>
      <div className="recording-settings video-settings__modal" onClick={(e) => e.stopPropagation()}>
        <div className="recording-settings__header">
          <h2 className="recording-settings__title">
            {t("recording.videoSettingsTitle")}
          </h2>
          <button
            type="button"
            className="recording-settings__close"
            onClick={onClose}
            aria-label={t("recording.close")}
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <div className="recording-settings__divider" />

        {/* Video quality */}
        <section className="recording-settings__section">
          <label className="recording-settings__label">
            {t("recording.videoQuality")}
          </label>
          <div className="recording-settings__size-row">
            <div className="recording-settings__size-btns">
              {QUALITY_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`recording-settings__size-btn ${
                    quality === opt.value ? "recording-settings__size-btn--active" : ""
                  }`}
                  onClick={() => setQuality(opt.value)}
                >
                  {t(opt.labelKey)}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Target duration: presets + custom on one row, custom on the far right */}
        <section className="recording-settings__section">
          <label className="recording-settings__label">
            {t("recording.videoTargetDuration")}
          </label>
          <div className="video-settings__duration-row">
            <div className="recording-settings__size-btns">
              <button
                type="button"
                className={`recording-settings__size-btn ${
                  !customDurationInput.trim() && targetDurationMinutes === DURATION_NO_PREFERENCE
                    ? "recording-settings__size-btn--active"
                    : ""
                }`}
                onClick={() => handlePresetDurationClick(DURATION_NO_PREFERENCE)}
              >
                {t("recording.videoDurationNoPreference")}
              </button>
              {DURATION_OPTIONS.filter((m): m is number => m !== null).map((m) => (
                <button
                  key={m}
                  type="button"
                  className={`recording-settings__size-btn ${
                    !customDurationInput.trim() && targetDurationMinutes === m
                      ? "recording-settings__size-btn--active"
                      : ""
                  }`}
                  onClick={() => handlePresetDurationClick(m)}
                >
                  {m} {t("recording.videoDurationUnit")}
                </button>
              ))}
            </div>
            <div className="video-settings__duration-custom-wrap">
              <input
                type="number"
                min={0.5}
                max={30}
                step={0.5}
                placeholder="—"
                className="video-settings__duration-input"
                value={customDurationInput}
                onChange={handleCustomDurationChange}
                aria-label={t("recording.videoTargetDuration")}
              />
              <span className="video-settings__duration-unit" aria-hidden="true">
                {t("recording.videoDurationUnit")}
              </span>
            </div>
          </div>
        </section>

        {/* Voice */}
        <section className="recording-settings__section">
          <label className="recording-settings__label">
            {t("recording.videoVoice")}
          </label>
          <div className="video-settings__voice-list">
            {VOICE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`video-settings__voice-btn ${
                  voice === opt.value ? "video-settings__voice-btn--active" : ""
                }`}
                onClick={() => handleVoiceClick(opt.value)}
                title={voiceSampleUrlBuilder ? t("recording.videoVoiceClickPreview") : undefined}
              >
                <span>{t(opt.labelKey)}</span>
              </button>
            ))}
          </div>
        </section>

        <button
          type="button"
          className="recording-settings__done-btn"
          onClick={handleGenerate}
        >
          {t("recording.videoGenerate")}
        </button>
      </div>
    </div>
  );
};
